import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Loader2, Download, Printer, Save, User, Heart, Syringe, Stethoscope, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const DCFHealthReportForm = ({ open, onClose, childId, childName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('parent');
  
  const [formData, setFormData] = useState({
    child_last_name: '',
    child_first_name: '',
    child_middle_initial: '',
    child_birthdate: '',
    child_address: '',
    child_city: '',
    child_state: 'WI',
    child_zip: '',
    parent_name: '',
    parent_address: '',
    parent_city: '',
    parent_state: 'WI',
    parent_zip: '',
    special_health_concerns: '',
    has_milk_allergy: null,
    milk_substitute: '',
    has_food_allergies: null,
    food_allergies_details: '',
    allergy_treatment_plan: '',
    blood_lead_test_date: '',
    immunization_exemptions: '',
    health_professional: {
      name: '',
      signature: '',
      address: '',
      city: '',
      state: 'WI',
      zip_code: '',
      exam_date: ''
    }
  });

  useEffect(() => {
    if (open && childId) {
      loadData();
    }
  }, [open, childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/children/${childId}/dcf-health-report-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          health_professional: { ...prev.health_professional, ...response.data.health_professional }
        }));
      }
    } catch (error) {
      console.error('Error loading health report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/children/${childId}/dcf-health-report-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Health report saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save health report');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0060`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS0060_Health_Report_${childName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-0060`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateHealthProfessional = (field, value) => {
    setFormData(prev => ({
      ...prev,
      health_professional: {
        ...prev.health_professional,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Loading health report...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dcf-health-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-red-500" />
            DCF Child Health Report (DCF-F-CFS0060-E)
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Official Wisconsin DCF health report - {childName}
          </p>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-health-report-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Report
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-2" data-testid="download-health-report-btn">
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="parent" data-testid="tab-parent">
              <User className="w-4 h-4 mr-1" />
              Parent/Guardian
            </TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health">
              <Heart className="w-4 h-4 mr-1" />
              Health Info
            </TabsTrigger>
            <TabsTrigger value="provider" data-testid="tab-provider">
              <Stethoscope className="w-4 h-4 mr-1" />
              Provider
            </TabsTrigger>
          </TabsList>

          {/* PARENT/GUARDIAN SECTION */}
          <TabsContent value="parent" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Instructions:</strong> This section should be completed by the parent or guardian.
            </div>

            {/* Child Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Child's Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={formData.child_last_name}
                      onChange={(e) => setFormData({ ...formData, child_last_name: e.target.value })}
                      placeholder="Smith"
                      data-testid="child-last-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={formData.child_first_name}
                      onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })}
                      placeholder="Emma"
                      data-testid="child-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>MI</Label>
                    <Input
                      value={formData.child_middle_initial}
                      onChange={(e) => setFormData({ ...formData, child_middle_initial: e.target.value })}
                      placeholder="J"
                      maxLength={1}
                      className="w-16"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Birthdate (mm/dd/yyyy) *</Label>
                    <Input
                      type="date"
                      value={formData.child_birthdate}
                      onChange={(e) => setFormData({ ...formData, child_birthdate: e.target.value })}
                      data-testid="child-birthdate"
                    />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    value={formData.child_address}
                    onChange={(e) => setFormData({ ...formData, child_address: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.child_city}
                      onChange={(e) => setFormData({ ...formData, child_city: e.target.value })}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.child_state}
                      onChange={(e) => setFormData({ ...formData, child_state: e.target.value })}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.child_zip}
                      onChange={(e) => setFormData({ ...formData, child_zip: e.target.value })}
                      placeholder="53703"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parent/Guardian Information */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Parent or Guardian Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Parent/Guardian Name (Last, First, MI)</Label>
                  <Input
                    value={formData.parent_name}
                    onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                    placeholder="Smith, John"
                    data-testid="parent-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    value={formData.parent_address}
                    onChange={(e) => setFormData({ ...formData, parent_address: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.parent_city}
                      onChange={(e) => setFormData({ ...formData, parent_city: e.target.value })}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.parent_state}
                      onChange={(e) => setFormData({ ...formData, parent_state: e.target.value })}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.parent_zip}
                      onChange={(e) => setFormData({ ...formData, parent_zip: e.target.value })}
                      placeholder="53703"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HEALTH PROFESSIONAL SECTION - Health Info */}
          <TabsContent value="health" className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> This section should be completed by the health professional.
            </div>

            {/* Special Health Concerns */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Special Health Concerns
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Instructions for feeding and care of child with special health concerns</Label>
                  <Textarea
                    value={formData.special_health_concerns}
                    onChange={(e) => setFormData({ ...formData, special_health_concerns: e.target.value })}
                    placeholder="Describe any special health concerns and care instructions..."
                    rows={4}
                    data-testid="special-health-concerns"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card className="border-red-200">
              <CardHeader className="pb-2 bg-red-50 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2 text-red-800">
                  <Heart className="w-4 h-4" />
                  Allergies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {/* Milk Allergy */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="space-y-3">
                    <Label className="font-medium">Does the child have a milk allergy?</Label>
                    <RadioGroup
                      value={formData.has_milk_allergy === true ? 'yes' : formData.has_milk_allergy === false ? 'no' : ''}
                      onValueChange={(v) => setFormData({ ...formData, has_milk_allergy: v === 'yes' })}
                      className="flex gap-4"
                      data-testid="milk-allergy"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="milk-yes" />
                        <Label htmlFor="milk-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="milk-no" />
                        <Label htmlFor="milk-no" className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                    {formData.has_milk_allergy && (
                      <div className="space-y-2 mt-3 p-3 bg-white rounded border">
                        <Label>Recommended milk substitute:</Label>
                        <Input
                          value={formData.milk_substitute}
                          onChange={(e) => setFormData({ ...formData, milk_substitute: e.target.value })}
                          placeholder="e.g., Soy milk, Almond milk"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Food/Non-Food Allergies */}
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="space-y-3">
                    <Label className="font-medium">Does this child have any food or non-food allergies?</Label>
                    <RadioGroup
                      value={formData.has_food_allergies === true ? 'yes' : formData.has_food_allergies === false ? 'no' : ''}
                      onValueChange={(v) => setFormData({ ...formData, has_food_allergies: v === 'yes' })}
                      className="flex gap-4"
                      data-testid="food-allergies"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="food-yes" />
                        <Label htmlFor="food-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="food-no" />
                        <Label htmlFor="food-no" className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                    {formData.has_food_allergies && (
                      <div className="space-y-3 mt-3 p-3 bg-white rounded border">
                        <div className="space-y-2">
                          <Label>Specify allergies:</Label>
                          <Textarea
                            value={formData.food_allergies_details}
                            onChange={(e) => setFormData({ ...formData, food_allergies_details: e.target.value })}
                            placeholder="List all food and non-food allergies..."
                            rows={2}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Treatment plan for allergic reaction:</Label>
                          <Textarea
                            value={formData.allergy_treatment_plan}
                            onChange={(e) => setFormData({ ...formData, allergy_treatment_plan: e.target.value })}
                            placeholder="Describe the treatment plan to be implemented in the event of an allergic reaction..."
                            rows={3}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Blood Lead Test */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Syringe className="w-4 h-4 text-blue-500" />
                  Blood Lead Test
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Date of child's most recent blood lead test (mm/dd/yyyy)</Label>
                  <Input
                    type="date"
                    value={formData.blood_lead_test_date}
                    onChange={(e) => setFormData({ ...formData, blood_lead_test_date: e.target.value })}
                    data-testid="blood-lead-date"
                  />
                </div>
                <p className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
                  <strong>Note:</strong> Children on Medicaid are required to be tested at around ages 12 months and 24 months 
                  or once between the ages of 3 and 5 years if no previous test is documented. Lead testing is optional 
                  for children who are not on Medicaid.
                </p>
              </CardContent>
            </Card>

            {/* Immunizations */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Syringe className="w-4 h-4 text-green-500" />
                  Immunizations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Immunization(s) not to be administered to child due to medical reason(s) - Specify:</Label>
                  <Textarea
                    value={formData.immunization_exemptions}
                    onChange={(e) => setFormData({ ...formData, immunization_exemptions: e.target.value })}
                    placeholder="List any immunizations that should not be given and the medical reason..."
                    rows={3}
                    data-testid="immunization-exemptions"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HEALTH PROFESSIONAL AUTHORIZATION */}
          <TabsContent value="provider" className="space-y-4 mt-4">
            <Card className="border-primary">
              <CardHeader className="pb-2 bg-primary/5 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-primary" />
                  Health Professional Authorization
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  I certify that I have examined the above child on this date and that he/she is able to participate in child care activities.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name - MD, PA, or other EPSDT Provider *</Label>
                    <Input
                      value={formData.health_professional.name}
                      onChange={(e) => updateHealthProfessional('name', e.target.value)}
                      placeholder="Dr. Johnson"
                      data-testid="provider-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date of Examination *</Label>
                    <Input
                      type="date"
                      value={formData.health_professional.exam_date}
                      onChange={(e) => updateHealthProfessional('exam_date', e.target.value)}
                      data-testid="exam-date"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>SIGNATURE - MD, PA, or other EPSDT Provider *</Label>
                  <Input
                    value={formData.health_professional.signature}
                    onChange={(e) => updateHealthProfessional('signature', e.target.value)}
                    placeholder="Type full name as signature"
                    className="font-cursive text-lg"
                    data-testid="provider-signature"
                  />
                  <p className="text-xs text-muted-foreground">
                    By typing the name, you acknowledge this as an electronic signature.
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Provider Address</Label>
                  <Input
                    value={formData.health_professional.address}
                    onChange={(e) => updateHealthProfessional('address', e.target.value)}
                    placeholder="456 Medical Center Dr"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.health_professional.city}
                      onChange={(e) => updateHealthProfessional('city', e.target.value)}
                      placeholder="Madison"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.health_professional.state}
                      onChange={(e) => updateHealthProfessional('state', e.target.value)}
                      placeholder="WI"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={formData.health_professional.zip_code}
                      onChange={(e) => updateHealthProfessional('zip_code', e.target.value)}
                      placeholder="53705"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-health-report-final-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Health Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DCFHealthReportForm;
